import { z } from 'zod';

// Contrato de la pantalla Notificaciones (so=370) validado con Zod (ENG-VAL-01): un drift
// del backend (so=330 inbox y configuración, so=410 vencimientos) cae al estado de error
// de la query, nunca a un throw en render.

export const RULE_CODES = [
  'stage.pending',
  'stage.half_elapsed',
  'stage.overdue',
  'escalation',
  'review.upcoming',
  // Reunión 2026-10-27: fecha de un campo propio (los días los pone cada campo).
  'custom_date.upcoming',
  'custom_date.overdue',
  // G3: solicitudes de documento (solo activa y canales, como stage.pending).
  'request.submitted',
  'request.resolved',
  // G2-C: capacitación sin reporte del LMS (solo activa y canales, como stage.pending).
  'training.lms_unreported',
] as const;
export type RuleCode = (typeof RULE_CODES)[number];

export const CHANNELS = ['in_app', 'email'] as const;
export type Channel = (typeof CHANNELS)[number];

export const REPEAT_VALUES = ['daily', 'weekly', 'biweekly', 'monthly'] as const;
export type RepeatEvery = (typeof REPEAT_VALUES)[number];

export const ESCALATION_TARGETS = ['department_supervisors', 'quality', 'none'] as const;
export type EscalationTarget = (typeof ESCALATION_TARGETS)[number];

export const DEADLINE_STAGES = ['elaboracion', 'revision', 'aprobacion', 'capacitacion'] as const;
export type DeadlineStage = (typeof DEADLINE_STAGES)[number];

export const inboxItemSchema = z.object({
  id: z.string(),
  companyId: z.string(),
  createdAt: z.string(),
  subject: z.string(),
  body: z.string(),
  actionUrl: z.string().nullable(),
  severity: z.string(),
  status: z.string(),
  readAt: z.string().nullable(),
  actedAt: z.string().nullable(),
  dismissedAt: z.string().nullable(),
  pendingAction: z.boolean(),
  escalationLevel: z.number(),
});
export type InboxItem = z.infer<typeof inboxItemSchema>;

export const inboxPageSchema = z.object({
  success: z.literal(true),
  data: z.array(inboxItemSchema),
  meta: z.object({ next_cursor: z.string().nullable() }),
});

export const markAllReadSchema = z.object({
  success: z.literal(true),
  data: z.object({ updated: z.number().int().nonnegative() }),
});

export const ruleSchema = z.object({
  code: z.enum(RULE_CODES),
  name: z.string(),
  description: z.string(),
  enabled: z.boolean(),
  channels: z.array(z.enum(CHANNELS)),
  repeatEvery: z.enum(REPEAT_VALUES).optional(),
  leadDays: z.number().optional(),
  thresholdPct: z.number().optional(),
  escalateAfterDays: z.number().optional(),
  escalationTarget: z.enum(ESCALATION_TARGETS).optional(),
});
export type NotificationRule = z.infer<typeof ruleSchema>;

export const rulesResponseSchema = z.object({
  success: z.literal(true),
  data: z.array(ruleSchema),
});

export const stageDeadlinesSchema = z.object({
  elaboracion: z.number(),
  revision: z.number(),
  aprobacion: z.number(),
  capacitacion: z.number(),
});
export type StageDeadlines = z.infer<typeof stageDeadlinesSchema>;

export const stageDeadlinesResponseSchema = z.object({
  success: z.literal(true),
  data: stageDeadlinesSchema,
});

export const escalatedItemSchema = z.object({
  entry: z.object({ id: z.string(), code: z.string(), title: z.string().nullable() }),
  stage: z.string().nullable(),
  daysLeft: z.number(),
  escalatedTo: z.array(z.object({ id: z.string(), name: z.string() })),
});
export type EscalatedItem = z.infer<typeof escalatedItemSchema>;

export const escalationsResponseSchema = z.object({
  success: z.literal(true),
  data: z.object({ total: z.number(), items: z.array(escalatedItemSchema) }),
});
export type ActiveEscalations = z.infer<typeof escalationsResponseSchema>['data'];

/** Cuerpo del PUT de una regla: cada regla lleva solo sus campos (el backend es estricto). */
export type RuleUpdate = Pick<NotificationRule, 'enabled' | 'channels'> &
  Partial<
    Pick<
      NotificationRule,
      'repeatEvery' | 'leadDays' | 'thresholdPct' | 'escalateAfterDays' | 'escalationTarget'
    >
  >;
